import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { getProgress } from '../progress/progress.utils';
import {
  areContentItemsEqual,
  areContextsEqual,
  getTemplateEntry,
  ICON_POSITION,
  makeTextPatch,
} from '../utils/templates';
import Incentive from '../incentive/incentive.component';
import { HEIGHT as INCENTIVE_HEIGHT } from '../incentive/incentive.style';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import {
  defaultContext,
  getInitialStyle,
  getFocusedStyle,
  colors,
  getIconViewBox,
  getIcon,
  unFocusedStyle,
  IMAGE_OFFSET,
  TITLES_ORDER,
  IMAGE_MARGIN_BOTTOM,
  PROGRESS_HEIGHT,
  ICON_PADDING_VERTICAL,
} from './squareMeta.style';
import { sizes, templates, imageOptions, ICONS_TYPES } from './squareMeta.constants';
import { getLinesCount } from '../incentive/incentive.utils';

/**
 * The SquareMeta is used to display an audio content.
 *
 * @version 1.1.0
 * @see https://zeroheight.com/726d5c282/v/latest/p/887d56-squaremeta--v110
 */
export default class SquareMeta extends Lightning.Component {
  /* istanbul ignore next */
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        Incentive: {
          type: Incentive,
        },
        Title: {},
        Description: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._style = { ...defaultContext };
    this._metadataPositionHandler = this._updateMetadataPosition.bind(this);
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  // NOTE: the listeners are bound to the attachment lifecycle rather than to the data, since a recycled view
  // applies data many times but is attached only once
  _attach() {
    TITLES_ORDER.forEach(id => {
      const view = this.tag(id);
      if (view) {
        view.on('txLoaded', this._metadataPositionHandler);
      }
    });
  }

  _detach() {
    TITLES_ORDER.forEach(id => {
      const view = this.tag(id);
      if (view) {
        view.removeListener('txLoaded', this._metadataPositionHandler);
      }
    });
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get index() {
    return this._index;
  }

  set index(value) {
    this._index = value;
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = defaultContext.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    const progress = getProgress(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      // A three level patch object to move one number: the shader is already there, so it is written directly
      this.tag('Image').shader.progress = progress;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize: this._style.screenSize, colorMode: colorScheme };
    }

    const { screenSize, colorMode } = this._style;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);

    const { title, description, pictos, incentives, image } = this._item;
    const { wordWrapWidth } = this;
    const { PRIMARY: primaryImage } = getTemplateEntry(imageOptions, template, renderingContext, screenSize);

    const imageUrl = (image && image.src) || '';
    const offset = IMAGE_OFFSET[screenSize];
    const viewBox = getIconViewBox(screenSize);

    const topLeftIcon = getIcon(pictos, ICON_POSITION.TOP_LEFT);
    const topRightIcon = getIcon(pictos, ICON_POSITION.TOP_RIGHT);
    const bottomRightIcon = getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT);

    const incentiveLines = getLinesCount(incentives);
    this._hasIncentives = incentiveLines > 0;

    this.patch({
      w: this._config.w,
      h: this._config.h,
      Border: {
        w: primaryImage.width + offset,
        h: primaryImage.height + offset,
      },
      Image: {
        w: primaryImage.width,
        h: primaryImage.height,
        src: imageUrl,
        shader: {
          progress,
        },
      },
      TopLeftIcon: {
        visible: Boolean(topLeftIcon),
        iconData: {
          picto: topLeftIcon,
          viewBox,
        },
      },
      TopRightIcon: {
        visible: Boolean(topRightIcon),
        iconData: {
          picto: topRightIcon,
          viewBox,
        },
      },
      BottomRightIcon: {
        visible: Boolean(bottomRightIcon),
        y: primaryImage.height - ICON_PADDING_VERTICAL - PROGRESS_HEIGHT,
        iconData: {
          picto: bottomRightIcon,
          viewBox,
        },
      },
      Incentive: {
        visible: this._hasIncentives,
        y: primaryImage.height + IMAGE_MARGIN_BOTTOM,
        data: {
          w: primaryImage.width,
          linesCount: incentiveLines,
          incentives,
          colorMode,
        },
      },
      Title: makeTextPatch(title || '', wordWrapWidth, colors.title[colorMode]),
      Description: makeTextPatch(description || '', wordWrapWidth, colors.description[colorMode]),
    });
  }

  get wordWrapWidth() {
    return this._config ? this._config.w : 0;
  }

  _updateMetadataPosition() {
    // NOTE: we are using manual calculation instead of the "flex" layout in order to speed up tile creation
    const [firstLineTag, secondLineTag] = TITLES_ORDER;
    const firstLine = this.tag(firstLineTag);
    const secondLine = this.tag(secondLineTag);
    const incentiveHeight = this._hasIncentives ? INCENTIVE_HEIGHT[this._style.screenSize] : 0;

    let offset = this.tag('Image').h + IMAGE_MARGIN_BOTTOM + incentiveHeight;

    if (firstLine.visible) {
      firstLine.y = offset;
      offset += firstLine.finalH;
    }

    if (secondLine.visible) {
      secondLine.y = offset;
    }
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

SquareMeta.Templates = templates;
SquareMeta.Sizes = sizes;
SquareMeta.ImageOptions = imageOptions;
